import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { 
  ArrowLeft,
  Calendar,
  Clock,
  ChevronRight,
  Loader2,
  Sun,
  Sunset,
  Moon,
  IndianRupee,
  Check,
  X,
  AlertCircle,
  Users,
  Flame,
  Zap,
  CircleAlert,
  Ban,
  Phone
} from 'lucide-react';
import CustomerFooter from '../../components/CustomerFooter';

import { BACKEND_URL, API } from '../../utils/apiConfig';

// Time categories with vibrant colors
const getTimeCategory = (hour) => {
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};

const TIME_CATEGORIES = {
  morning: { 
    label: 'Morning', 
    icon: Sun, 
    bgColor: 'bg-gradient-to-r from-amber-400 to-orange-400',
    textColor: 'text-amber-600',
    lightBg: 'bg-amber-50',
    slots: '6 AM - 12 PM' 
  },
  afternoon: { 
    label: 'Afternoon', 
    icon: Sun, 
    bgColor: 'bg-gradient-to-r from-orange-400 to-rose-400',
    textColor: 'text-orange-600',
    lightBg: 'bg-orange-50',
    slots: '12 PM - 5 PM' 
  },
  evening: { 
    label: 'Evening', 
    icon: Sunset, 
    bgColor: 'bg-gradient-to-r from-rose-400 to-purple-500',
    textColor: 'text-rose-600',
    lightBg: 'bg-rose-50',
    slots: '5 PM - 9 PM' 
  },
  night: { 
    label: 'Night', 
    icon: Moon, 
    bgColor: 'bg-gradient-to-r from-purple-500 to-indigo-600',
    textColor: 'text-indigo-600',
    lightBg: 'bg-indigo-50',
    slots: '9 PM - 2 AM' 
  }
};

// Occupancy-based urgency messaging
const getOccupancyInfo = (categorySlots, totalCourts, isEvening = false) => {
  if (!categorySlots.length || !totalCourts) {
    return { message: 'Available', icon: null, badgeClass: 'bg-emerald-50 text-emerald-600', dotClass: 'bg-emerald-400' };
  }

  const totalCapacity = categorySlots.length * totalCourts;
  const bookedCount = categorySlots.reduce((sum, slot) => {
    return sum + (totalCourts - (slot.courts_available || 0));
  }, 0);
  const pct = (bookedCount / totalCapacity) * 100;

  // Avoid misleading urgency for very few bookings
  if (bookedCount <= 1 && pct < 26) {
    return { message: 'Available', icon: null, badgeClass: 'bg-emerald-50 text-emerald-600', dotClass: 'bg-emerald-400' };
  }

  // Peak hour multiplier for evening — nudge thresholds down by 1.3x
  const adj = isEvening ? Math.min(pct * 1.3, 100) : pct;

  if (adj === 0)  return { message: 'Available',       icon: null,        badgeClass: 'bg-emerald-50 text-emerald-600',      dotClass: 'bg-emerald-400' };
  if (adj <= 25)  return { message: 'Filling Fast',    icon: Flame,       badgeClass: 'bg-amber-50 text-amber-700 border border-amber-200',  dotClass: 'bg-amber-400' };
  if (adj <= 60)  return { message: 'Booking Fast',    icon: Zap,         badgeClass: 'bg-orange-50 text-orange-700 border border-orange-200', dotClass: 'bg-orange-400' };
  if (adj <= 90)  return { message: 'Almost Full',     icon: CircleAlert, badgeClass: 'bg-red-50 text-red-700 border border-red-200',         dotClass: 'bg-red-400' };
  if (adj < 100)  return { message: 'Last Few Spots',  icon: CircleAlert, badgeClass: 'bg-red-100 text-red-800 border border-red-300 font-semibold', dotClass: 'bg-red-500 animate-pulse' };
  return           { message: 'Fully Booked',   icon: Ban,         badgeClass: 'bg-slate-100 text-slate-500',        dotClass: 'bg-slate-400' };
};

// Category end-hour thresholds (hide entire category if IST hour >= this value on today)
const CATEGORY_END_HOUR = { morning: 12, afternoon: 17, evening: 21, night: 30 };

const isCategoryPast = (key, selectedDate) => {
  // Only apply to today's date
  const now = new Date();
  const istOffset = 5.5 * 60; // IST = UTC+5:30
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const istDate = new Date(utcMs + istOffset * 60000);
  const todayStr = istDate.toISOString().split('T')[0]; // YYYY-MM-DD
  if (selectedDate !== todayStr) return false;
  const istHour = istDate.getHours();
  return istHour >= (CATEGORY_END_HOUR[key] || 30);
};

export default function SlotSelectionPage() {
  const { isProfileComplete } = useCustomerAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const selectedGame = searchParams.get('game');
  const venueId = searchParams.get('venue_id');
  const venueName = searchParams.get('venue_name');
  const initialDate = searchParams.get('date'); // Date from NLP or direct navigation
  
  // Initialize with URL date if provided, otherwise today
  const getInitialDate = () => {
    if (initialDate) {
      // Validate the date is within booking range
      const parsed = new Date(initialDate);
      const today = new Date();
      const maxDate = addDays(today, 15);
      if (parsed >= today && parsed <= maxDate) {
        return initialDate;
      }
    }
    return format(new Date(), 'yyyy-MM-dd');
  };
  
  const [selectedDate, setSelectedDate] = useState(getInitialDate());
  const [slots, setSlots] = useState([]);
  const [maxBookingDays, setMaxBookingDays] = useState(15);
  const [loading, setLoading] = useState(true);
  const [selectedSlots, setSelectedSlots] = useState([]); // Array of selected slots
  const [expandedCategory, setExpandedCategory] = useState('evening');
  const [selectedDuration, setSelectedDuration] = useState(null); // Duration preset (60, 90, 120 mins)
  const [numCourts, setNumCourts] = useState(1); // Number of courts (1-3)
  const [totalCourtsAvailable, setTotalCourtsAvailable] = useState(0);
  const [bookingControl, setBookingControl] = useState(null);
  
  // Get time preference from URL (for NLP navigation)
  const timePreference = searchParams.get('time');

  // Duration presets
  const DURATION_PRESETS = [
    { mins: 60, slots: 2, label: '60 min' },
    { mins: 90, slots: 3, label: '90 min' },
    { mins: 120, slots: 4, label: '2 hrs' }
  ];

  // Generate date options (today + next 14 days = 15 days total)
  const dateOptions = Array.from({ length: maxBookingDays }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      date: format(date, 'yyyy-MM-dd'),
      day: format(date, 'EEE'),
      dayNum: format(date, 'd'),
      month: format(date, 'MMM'),
      isToday: i === 0
    };
  });

  useEffect(() => {
    if (!isProfileComplete) {
      navigate('/profile-setup');
      return;
    }
    
    if (!selectedGame || !venueId) {
      navigate('/');
      return;
    }
    
    fetchSlots();
  }, [isProfileComplete, selectedGame, venueId, selectedDate, numCourts, navigate]);

  useEffect(() => {
    axios.get(`${API}/booking-control/status`)
      .then(res => setBookingControl(res.data))
      .catch(() => {});
  }, []);

  const fetchSlots = async () => {
    setLoading(true);
    setSelectedSlots([]);
    
    try {
      const response = await axios.get(`${API}/user/slots`, {
        params: {
          venue_id: venueId,
          game: selectedGame,
          date: selectedDate,
          num_courts: numCourts
        }
      });
      setSlots(response.data.slots || []);
      if (response.data.max_booking_days) {
        setMaxBookingDays(response.data.max_booking_days);
      }
      if (response.data.total_courts) {
        setTotalCourtsAvailable(response.data.total_courts);
      }
      
      // Auto-expand category based on time preference from NLP, or first available
      const categorized = categorizeSlots(response.data.slots || []);
      
      // Map time preference to category
      const timeToCategory = {
        'early_morning': 'morning',
        'morning': 'morning',
        'afternoon': 'afternoon',
        'evening': 'evening',
        'night': 'night'
      };
      
      // If time preference was passed (from NLP), expand that category
      const preferredCategory = timePreference ? timeToCategory[timePreference] || 'evening' : null;
      
      if (preferredCategory && !isCategoryPast(preferredCategory, selectedDate) && categorized[preferredCategory]?.some(s => s.available)) {
        setExpandedCategory(preferredCategory);
      } else {
        // Fallback to first category with available slots that hasn't passed
        const firstAvailable = ['evening', 'afternoon', 'morning', 'night'].find(
          cat => !isCategoryPast(cat, selectedDate) && categorized[cat]?.some(s => s.available)
        );
        if (firstAvailable) {
          setExpandedCategory(firstAvailable);
        }
      }
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to load available slots';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const categorizeSlots = (slotsData) => {
    const categories = {
      morning: [],
      afternoon: [],
      evening: [],
      night: []
    };
    
    slotsData.forEach(slot => {
      const category = getTimeCategory(slot.hour);
      categories[category].push(slot);
    });
    
    return categories;
  };

  // Convert time string to minutes for comparison
  const timeToMinutes = (timeStr) => {
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return 0;
    let hour = parseInt(match[1]);
    const minute = parseInt(match[2]);
    const period = match[3].toUpperCase();
    
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    
    return hour * 60 + minute;
  };

  // Check if a slot can be added (must be consecutive to existing selection)
  const canSelectSlot = (slot) => {
    if (!slot.available) return false;
    if (selectedSlots.length === 0) return true;
    
    const slotStart = timeToMinutes(slot.start_time);
    const slotEnd = timeToMinutes(slot.end_time);
    
    // Sort existing selected slots
    const sortedSelected = [...selectedSlots].sort(
      (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
    );
    
    const firstSelectedStart = timeToMinutes(sortedSelected[0].start_time);
    const lastSelectedEnd = timeToMinutes(sortedSelected[sortedSelected.length - 1].end_time);
    
    // Can add if slot is adjacent to start or end of current selection
    return slotEnd === firstSelectedStart || slotStart === lastSelectedEnd;
  };

  const isSlotSelected = (slot) => {
    return selectedSlots.some(s => 
      s.start_time === slot.start_time && s.end_time === slot.end_time
    );
  };

  const handleSlotSelect = (slot) => {
    if (!slot.available) return;
    
    if (isSlotSelected(slot)) {
      // Remove from selection - but only if it's at the edge
      const slotStart = timeToMinutes(slot.start_time);
      const slotEnd = timeToMinutes(slot.end_time);
      
      const sortedSelected = [...selectedSlots].sort(
        (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time)
      );
      
      const firstStart = timeToMinutes(sortedSelected[0].start_time);
      const lastEnd = timeToMinutes(sortedSelected[sortedSelected.length - 1].end_time);
      
      // Only allow removing from edges to maintain consecutiveness
      if (slotStart === firstStart || slotEnd === lastEnd) {
        setSelectedSlots(prev => prev.filter(s => 
          !(s.start_time === slot.start_time && s.end_time === slot.end_time)
        ));
      } else {
        toast.error('Can only remove slots from the edges of your selection');
      }
    } else {
      // Check if can add (must be consecutive)
      if (!canSelectSlot(slot)) {
        toast.error('Please select consecutive time slots only');
        return;
      }
      
      // Add to selection
      setSelectedSlots(prev => [...prev, {
        start_time: slot.start_time,
        end_time: slot.end_time,
        price: slot.price || 0
      }]);
    }
  };

  const clearSelection = () => {
    setSelectedSlots([]);
    setSelectedDuration(null);
  };

  // Handle duration preset selection - auto-selects consecutive slots from a clicked slot
  const handleDurationSelect = (duration) => {
    if (selectedDuration === duration) {
      // Deselect if same duration clicked
      setSelectedDuration(null);
      return;
    }
    setSelectedDuration(duration);
    // Clear current selection when changing duration
    setSelectedSlots([]);
  };

  // When duration is selected and user clicks a slot, auto-select consecutive slots
  const handleSlotWithDuration = (slot) => {
    if (!slot.available) return;
    
    if (selectedDuration) {
      // Find consecutive available slots starting from this slot
      const requiredSlots = DURATION_PRESETS.find(d => d.mins === selectedDuration)?.slots || 2;
      const slotStartMins = timeToMinutes(slot.start_time);
      
      // Get all slots sorted by time
      const sortedSlots = [...slots].sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));
      const startIdx = sortedSlots.findIndex(s => s.start_time === slot.start_time);
      
      if (startIdx === -1) return;
      
      // Try to select consecutive slots forward
      const slotsToSelect = [];
      for (let i = startIdx; i < sortedSlots.length && slotsToSelect.length < requiredSlots; i++) {
        const currentSlot = sortedSlots[i];
        if (!currentSlot.available) break;
        
        // Check if consecutive
        if (slotsToSelect.length > 0) {
          const lastSlot = slotsToSelect[slotsToSelect.length - 1];
          const lastEnd = timeToMinutes(lastSlot.end_time);
          const currentStart = timeToMinutes(currentSlot.start_time);
          if (currentStart !== lastEnd) break;
        }
        
        slotsToSelect.push({
          start_time: currentSlot.start_time,
          end_time: currentSlot.end_time,
          price: currentSlot.price || 0
        });
      }
      
      if (slotsToSelect.length < requiredSlots) {
        toast.error(`Not enough consecutive slots available. Need ${requiredSlots} slots (${selectedDuration} mins).`);
        return;
      }
      
      setSelectedSlots(slotsToSelect);
      toast.success(`Selected ${selectedDuration} mins (${requiredSlots} slots)`);
    } else {
      // Normal slot selection
      handleSlotSelect(slot);
    }
  };

  const totalAmount = selectedSlots.reduce((sum, slot) => sum + (slot.price || 0), 0) * numCourts;
  
  // Get average price for display (from first available slot)
  const displayPrice = slots.find(s => s.available)?.price || 0;

  // Minimum slots required (60 mins = 2 slots of 30 mins each)
  const MIN_SLOTS_REQUIRED = 2;

  // Centralized booking-control gate (per weekday of selected slot date)
  const BC_WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  const isDayBlocked = (dateStr) => {
    if (!bookingControl || !dateStr) return false;
    const { master_enabled, channels, days } = bookingControl;
    if (master_enabled === false) return true;
    if (channels && channels.app === false) return true;
    const [y, mo, d] = dateStr.split('-').map(Number);
    const wk = BC_WEEKDAY_KEYS[new Date(y, mo - 1, d).getDay()];
    if (days && days[wk] === false) return true;
    return false;
  };
  const dayBlocked = isDayBlocked(selectedDate);
  const contactNumber = bookingControl?.contact_number || '9429694401';

  const handleContinue = () => {
    if (dayBlocked) {
      toast.error(`Bookings are closed for the selected day. Please call ${contactNumber}.`);
      return;
    }
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }
    
    if (selectedSlots.length < MIN_SLOTS_REQUIRED) {
      toast.error(`Minimum booking is ${MIN_SLOTS_REQUIRED * 30} minutes (${MIN_SLOTS_REQUIRED} consecutive slots)`);
      return;
    }
    
    // Sort slots by time
    const sortedSlots = [...selectedSlots].sort((a, b) => {
      const timeA = a.start_time;
      const timeB = b.start_time;
      return timeA.localeCompare(timeB);
    });
    
    const params = new URLSearchParams({
      game: selectedGame,
      venue_id: venueId,
      venue_name: venueName,
      date: selectedDate,
      slots: JSON.stringify(sortedSlots),
      total: totalAmount.toString(),
      num_courts: numCourts.toString()
    });
    
    navigate(`/confirm?${params.toString()}`);
  };

  const categorizedSlots = categorizeSlots(slots);

  // Calculate duration based on selected slots
  const getDuration = () => {
    return selectedSlots.length * 30; // 30 mins per slot
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 pb-36">
      {/* Progress Steps - Vibrant */}
      <div className="bg-white/80 backdrop-blur border-b border-purple-100 px-4 py-3">
        <div className="flex items-center gap-1 text-xs">
          <span className="flex items-center gap-1 text-emerald-600">
            <span className="w-6 h-6 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-white flex items-center justify-center text-xs font-bold shadow-md">✓</span>
            <span className="hidden sm:inline">Game</span>
          </span>
          <div className="flex-1 h-1 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full" />
          <span className="flex items-center gap-1 text-cyan-600">
            <span className="w-6 h-6 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white flex items-center justify-center text-xs font-bold shadow-md">✓</span>
            <span className="hidden sm:inline">Venue</span>
          </span>
          <div className="flex-1 h-1 bg-gradient-to-r from-blue-400 to-violet-500 rounded-full" />
          <span className="flex items-center gap-1 text-violet-600 font-medium">
            <span className="w-6 h-6 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 text-white flex items-center justify-center text-xs font-bold shadow-lg ring-2 ring-violet-200">3</span>
            <span className="hidden sm:inline">Slot</span>
          </span>
          <div className="flex-1 h-1 bg-slate-200 rounded-full" />
          <span className="flex items-center gap-1 text-slate-400">
            <span className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs">4</span>
            <span className="hidden sm:inline">Confirm</span>
          </span>
        </div>
      </div>

      {/* Context Bar */}
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-purple-200 text-xs">Booking for</p>
            <p className="font-semibold">{selectedGame} • {venueName}</p>
          </div>
          {displayPrice > 0 && (
            <div className="text-right">
              <p className="text-purple-200 text-xs">From</p>
              <p className="font-bold flex items-center gap-0.5">
                <IndianRupee className="w-4 h-4" />
                {displayPrice}/slot
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Court Count Selector */}
      {totalCourtsAvailable > 1 && (
        <div className="bg-white/80 backdrop-blur border-b border-purple-100 px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-medium text-slate-700">Number of Courts</span>
          </div>
          <div className="flex gap-2">
            {[1, 2, 3].filter(n => n <= totalCourtsAvailable).map((count) => (
              <button
                key={count}
                onClick={() => {
                  setNumCourts(count);
                  setSelectedSlots([]);
                  setSelectedDuration(null);
                }}
                className={`flex-1 py-2.5 px-3 rounded-xl text-center transition-all ${
                  numCourts === count
                    ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105'
                    : 'bg-white text-slate-600 hover:bg-purple-50 border border-purple-200'
                }`}
                data-testid={`court-count-${count}`}
              >
                <p className="font-bold text-sm">{count} Court{count > 1 ? 's' : ''}</p>
                <p className={`text-xs ${numCourts === count ? 'text-purple-200' : 'text-slate-400'}`}>
                  {count === 1 ? 'Single' : count === 2 ? 'Side by side' : 'Triple'}
                </p>
              </button>
            ))}
          </div>
          {numCourts > 1 && (
            <p className="text-xs text-purple-600 mt-2 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {numCourts} adjacent courts will be auto-assigned
            </p>
          )}
        </div>
      )}

      {/* Date Selector - Vibrant */}
      <div className="bg-white/60 backdrop-blur border-b border-purple-100 px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium text-slate-700">Select Date</span>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {dateOptions.map((option, idx) => (
            <button
              key={option.date}
              onClick={() => {
                setSelectedDate(option.date);
                setSelectedDuration(null);
              }}
              className={`flex-shrink-0 w-14 py-2 rounded-xl text-center transition-all ${
                selectedDate === option.date
                  ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105'
                  : 'bg-white text-slate-600 hover:bg-purple-50 border border-purple-100'
              }`}
              data-testid={`date-${option.date}`}
            >
              <p className={`text-xs ${selectedDate === option.date ? 'text-purple-200' : 'text-slate-400'}`}>
                {option.isToday ? 'Today' : option.day}
              </p>
              <p className="text-lg font-bold">{option.dayNum}</p>
              <p className={`text-xs ${selectedDate === option.date ? 'text-purple-200' : 'text-slate-400'}`}>
                {option.month}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Booking Closed Banner */}
      {dayBlocked && (
        <div className="px-4 pt-3">
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="booking-closed-banner">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <p className="font-semibold">Bookings closed for this day</p>
              <p className="text-red-700">Please call {contactNumber} to book for the selected date.</p>
            </div>
          </div>
        </div>
      )}

      {/* Duration Presets */}
      <div className="bg-white/80 backdrop-blur border-b border-purple-100 px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium text-slate-700">Quick Select Duration</span>
          {selectedDuration && (
            <button 
              onClick={() => setSelectedDuration(null)}
              className="ml-auto text-xs text-purple-600 hover:text-purple-800"
            >
              Clear
            </button>
          )}
        </div>
        <div className="flex gap-2">
          {DURATION_PRESETS.map((preset) => (
            <button
              key={preset.mins}
              onClick={() => handleDurationSelect(preset.mins)}
              className={`flex-1 py-2.5 px-3 rounded-xl text-center transition-all ${
                selectedDuration === preset.mins
                  ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105'
                  : 'bg-white text-slate-600 hover:bg-purple-50 border border-purple-200'
              }`}
              data-testid={`duration-${preset.mins}`}
            >
              <p className="font-bold text-sm">{preset.label}</p>
              <p className={`text-xs ${selectedDuration === preset.mins ? 'text-purple-200' : 'text-slate-400'}`}>
                {preset.slots} slots
              </p>
            </button>
          ))}
        </div>
        {selectedDuration && (
          <p className="text-xs text-purple-600 mt-2 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Tap any available slot to auto-select {selectedDuration} mins
          </p>
        )}
      </div>

      {/* Time Slots */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {Object.entries(TIME_CATEGORIES).map(([key, category]) => {
              const categorySlots = categorizedSlots[key] || [];
              const availableCount = categorySlots.filter(s => s.available).length;
              const selectedInCategory = categorySlots.filter(s => isSlotSelected(s)).length;
              const CategoryIcon = category.icon;
              const isEvening = key === 'evening';
              const occupancy = getOccupancyInfo(categorySlots, totalCourtsAvailable, isEvening);
              const OccupancyIcon = occupancy.icon;
              
              if (categorySlots.length === 0) return null;
              
              // Hide entire category if its time range has passed (today only, IST)
              if (isCategoryPast(key, selectedDate)) return null;
              
              return (
                <div key={key} className="bg-white rounded-2xl border border-purple-100 overflow-hidden shadow-sm">
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === key ? null : key)}
                    className={`w-full flex items-center justify-between p-4 transition-colors ${
                      expandedCategory === key ? category.lightBg : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${category.bgColor} flex items-center justify-center shadow-md`}>
                        <CategoryIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className={`font-semibold ${category.textColor}`}>{category.label}</p>
                        <p className="text-xs text-slate-500">{category.slots}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedInCategory > 0 && (
                        <span className="text-xs px-2 py-1 rounded-full bg-violet-600 text-white font-medium">
                          {selectedInCategory} selected
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${occupancy.badgeClass}`}
                            data-testid={`occupancy-${key}`}>
                        {OccupancyIcon && <OccupancyIcon className="w-3 h-3" />}
                        {occupancy.message}
                      </span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${
                        expandedCategory === key ? 'rotate-90' : ''
                      }`} />
                    </div>
                  </button>
                  
                  {expandedCategory === key && (
                    <div className="px-4 pb-4">
                      {selectedSlots.length > 0 && !selectedDuration && (
                        <p className="text-xs text-amber-600 mb-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Select consecutive slots only
                        </p>
                      )}
                      {selectedDuration && (
                        <p className="text-xs text-purple-600 mb-2 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Tap a start time for {selectedDuration} min session
                        </p>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        {categorySlots.map((slot, idx) => {
                          const selected = isSlotSelected(slot);
                          const canSelect = canSelectSlot(slot);
                          // In duration mode, all available slots are selectable as start points
                          const isSelectable = selectedDuration 
                            ? slot.available 
                            : slot.available && (selected || canSelect || selectedSlots.length === 0);
                          
                          // Per-slot occupancy indicator
                          const slotOccPct = totalCourtsAvailable > 0
                            ? ((totalCourtsAvailable - (slot.courts_available || 0)) / totalCourtsAvailable) * 100
                            : 0;
                          const slotUrgent = slot.available && slotOccPct > 60;
                          const slotCritical = slot.available && slotOccPct > 90;
                          
                          return (
                            <button
                              key={idx}
                              onClick={() => selectedDuration ? handleSlotWithDuration(slot) : handleSlotSelect(slot)}
                              disabled={!slot.available}
                              className={`relative py-3 px-2 rounded-xl text-center text-sm font-medium transition-all ${
                                selected
                                  ? 'bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-purple-500/30 scale-105 ring-2 ring-purple-300'
                                  : isSelectable
                                    ? slotCritical
                                      ? 'bg-gradient-to-br from-red-50 to-orange-50 text-red-700 hover:from-red-100 hover:to-orange-100 border border-red-200'
                                      : slotUrgent
                                        ? 'bg-gradient-to-br from-amber-50 to-yellow-50 text-amber-700 hover:from-amber-100 hover:to-yellow-100 border border-amber-200'
                                        : 'bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-700 hover:from-emerald-100 hover:to-teal-100 border border-emerald-200'
                                    : slot.available 
                                      ? 'bg-slate-50 text-slate-400 border border-slate-200 cursor-not-allowed opacity-60'
                                      : 'bg-slate-100 text-slate-400 cursor-not-allowed line-through'
                              }`}
                              data-testid={`slot-${slot.start_time.replace(/\s/g, '-')}`}
                            >
                              {selected && (
                                <Check className="absolute top-1 right-1 w-4 h-4 text-white" />
                              )}
                              {slotCritical && !selected && (
                                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                              )}
                              {slotUrgent && !slotCritical && !selected && (
                                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400" />
                              )}
                              {slot.start_time.replace(':00', '').replace(':30', ':30')}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-6 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200" />
            <span>Available</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 relative">
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-amber-400" />
            </div>
            <span>Filling Up</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 relative">
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-red-500" />
            </div>
            <span>Almost Full</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-gradient-to-br from-violet-500 to-purple-600" />
            <span>Selected</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-slate-200" />
            <span>Booked</span>
          </div>
        </div>
      </div>

      {/* Bottom CTA - Fixed */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-purple-100 shadow-2xl">
        {dayBlocked ? (
          <div className="p-4" data-testid="booking-closed-cta">
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-center">
              <p className="text-sm font-semibold text-red-700">Bookings are currently closed for the selected day.</p>
              <a href={`tel:${contactNumber}`} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-violet-700">
                <Phone className="w-4 h-4" /> Call {contactNumber} to book
              </a>
            </div>
          </div>
        ) : selectedSlots.length > 0 ? (
          <div className="p-4">
            {selectedSlots.length < MIN_SLOTS_REQUIRED && (
              <div className="mb-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Minimum {MIN_SLOTS_REQUIRED * 30} mins required. Select {MIN_SLOTS_REQUIRED - selectedSlots.length} more slot{MIN_SLOTS_REQUIRED - selectedSlots.length > 1 ? 's' : ''}.
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Selected</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    selectedSlots.length >= MIN_SLOTS_REQUIRED 
                      ? 'bg-violet-100 text-violet-700' 
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} • {getDuration()} mins{numCourts > 1 ? ` • ${numCourts} courts` : ''}
                  </span>
                  <button
                    onClick={clearSelection}
                    className="p-1 rounded-full hover:bg-red-50 text-red-500"
                    title="Clear selection"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {format(new Date(selectedDate), 'EEE, d MMM')}
                </p>
              </div>
              {totalAmount > 0 && (
                <div className="text-right">
                  <p className="text-xs text-slate-500">Total</p>
                  <p className="text-xl font-bold text-violet-600 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {totalAmount}
                  </p>
                </div>
              )}
            </div>
            <Button
              onClick={handleContinue}
              disabled={selectedSlots.length < MIN_SLOTS_REQUIRED}
              className={`w-full h-12 font-semibold rounded-xl shadow-lg ${
                selectedSlots.length >= MIN_SLOTS_REQUIRED
                  ? 'bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 hover:from-violet-600 hover:via-purple-600 hover:to-fuchsia-600 text-white shadow-purple-500/25'
                  : 'bg-slate-200 text-slate-500 cursor-not-allowed shadow-none'
              }`}
              data-testid="continue-btn"
            >
              {selectedSlots.length >= MIN_SLOTS_REQUIRED ? 'Continue to Confirm' : `Select ${MIN_SLOTS_REQUIRED - selectedSlots.length} more slot${MIN_SLOTS_REQUIRED - selectedSlots.length > 1 ? 's' : ''}`}
              {selectedSlots.length >= MIN_SLOTS_REQUIRED && <ChevronRight className="w-5 h-5 ml-1" />}
            </Button>
          </div>
        ) : (
          <div className="p-4 text-center">
            <p className="text-slate-500 text-sm">
              <Clock className="w-4 h-4 inline mr-1" />
              Tap slots to select • Minimum 60 mins (2 slots)
            </p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <CustomerFooter />
    </div>
  );
}
